import React from "react";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import {errorMessages} from "../../../../helpers/errorMessage";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {CountryService} from "../../../../common/services/countryService";
import {CountryModel} from "../../../../common/models/country";
import {routingAddresses} from "../../../../helpers/routing";
import {withRouter, RouteComponentProps} from "react-router";
import {Api} from "../../../../helpers/api";
import i18n from "../../../../i18n/i18n";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import Form from "../../../../common/components/shared/form/form";
import {Utils} from "../../../../helpers/utils";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import {PortModel} from "../../../models/ports";
import {PortService} from "../../../services/portService";
import Loader from "../../../../common/components/shared/loader/loader";

interface PortFormState {
    countries: CountryModel[];
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    countriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface PortFormProps extends RouteComponentProps {
    portData?: PortModel | null;
    isUpdate: boolean;
}

class PortForm extends React.Component <PortFormProps, PortFormState> {
    private name!: ValidatedValue<string>;
    private imoCode!: ValidatedValue<string>;
    private country!: ValidatedValue<string>

    constructor(props: any) {
        super(props);

        const portValidators = [{
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        }];

        this.name = new ValidatedValue("", portValidators);
        this.imoCode = new ValidatedValue("", portValidators);
        this.country = new ValidatedValue("", portValidators);

        const correctImoCodeValidators = 3;
        const portImoCodeValidators = [{
            name: "portImoCodeLengthIsCorrect",
            fn: () => Validator.maxLength(this.imoCode.value, correctImoCodeValidators),
            errorMessage: () => i18n.t("dictionaries.ports.errors.portImoCodeLengthIsCorrect", {number: correctImoCodeValidators})
        }];

        this.imoCode.addValidators(portImoCodeValidators);

        this.state = {
            countries: [],
            isSubmitButtonBlocked: false,
            response: undefined,
            countriesResponse: undefined
        };
    }

    async componentDidMount() {
        await this.getCountries();
        this.setPortData();
    }

    render() {
        if (this.state.countries.length > 0 || !Api.isSuccess(this.state.countriesResponse)) {
            return (
                <Form
                    renderError={this.renderErrors()}
                    renderContent={this.renderContent()}
                    backAddress={routingAddresses.dictionaries.ports.list}
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    submit={this.submit.bind(this)}
                />
            );
        } else {
            return <Loader/>;
        }
    }

    protected renderErrors() {
        if(!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage()}
                    translatedFields={{
                        name: i18n.t("dictionaries.ports.httpErrors.name"),
                        imoCode: i18n.t("dictionaries.ports.httpErrors.imoCode"),
                        country: i18n.t("dictionaries.ports.httpErrors.country")
                    }}
                    translatedFieldsForEmpty={{
                        name: i18n.t("dictionaries.ports.httpErrors.nameForEmpty"),
                        imoCode: i18n.t("dictionaries.ports.httpErrors.imoCodeForEmpty"),
                        country: i18n.t("dictionaries.ports.httpErrors.countryForEmpty")
                    }}
                    object={i18n.t("dictionaries.ports.httpErrors.object")}
                />
            );
        }
    }

    protected renderContent() {
        return (
            <>
                <TextInput label={i18n.t("dictionaries.ports.headers.name")} validatedValue={this.name} testId="port-name"/>
                <TextInput label={i18n.t("dictionaries.ports.headers.imoCode")} validatedValue={this.imoCode} testId="port-imo-code"/>
                <DropDown
                    label={i18n.t("dictionaries.ports.headers.country")}
                    validatedValue={this.country}
                    items={this.countriesToDropDownMap}
                    testId="port-country"
                    error={!Api.isSuccess(this.state.countriesResponse) ? i18n.t("errorMessages.countriesErrorSuffix") : ""}
                    reloadFunc={() => this.getCountries()}
                />
            </>
        );
    }

    private getErrorMessage() {
        return this.props.isUpdate
            ? i18n.t("dictionaries.ports.errors.modifyErrorSuffix")
            : i18n.t("dictionaries.ports.errors.addErrorSuffix");
    }

    public get countriesToDropDownMap() {
        return DropDownUtils.listToDropDownMap(this.state.countries, "imoCode");
    }

    public get isValid() {
        this.name.validate();
        this.imoCode.validate();
        this.country.validate();

        return this.name.isValid && this.imoCode.isValid && this.country.isValid;
    }

    private setPortData() {
        this.name.setValue(this.props.portData?.name || "");
        this.imoCode.setValue(this.props.portData?.imoCode || "");
        this.country.setValue(this.props.portData?.country?.imoCode || "");
    }

    private async submit() {
        this.setState({response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyPort();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.dictionaries.ports.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyPort(){
        if (this.props.isUpdate && this.props.portData?.id) {
            this.setState({
                response: await PortService.modifyPort({
                    name: this.name.value.trim(),
                    imoCode: this.imoCode.value.trim(),
                    countryImoCode: this.country.value,
                }, this.props.portData?.id)
            });
        } else {
            this.setState({
                response: await PortService.addPort({
                    name: this.name.value.trim(),
                    imoCode: this.imoCode.value.trim(),
                    countryImoCode: this.country.value,
                })
            });
        }
    }

    private async getCountries() {
        const countriesResponse = await CountryService.getAll();

        this.setState({
            countries: (countriesResponse)?.data?.data || [],
            countriesResponse
        });
    }
}

export default withRouter(PortForm);
